import { useState } from "react"
import { Music, ChevronDown, Check, Loader2, Search } from "lucide-react"

import { Button, Input } from "@aix/ui"
import { trpc } from "../../../lib/trpc"

interface Song {
  id: string
  title: string
  artist: string | null
  genre: string
  bpm: number | null
  camelotKey: string | null
  vocalsFileUrl: string | null
  instrumentalFileUrl: string | null
}

interface TrackSelectorProps {
  selectedSong: Song | null
  onSelect: (song: Song | null) => void
  trackType: "vocals" | "instrumental"
  excludeSongId?: string
  sourceBpm?: number | null
}

export function TrackSelector({ selectedSong, onSelect, trackType, excludeSongId, sourceBpm }: TrackSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")

  // For instrumental selection, get suggestions based on source BPM
  const { data: suggestionsData, isLoading: loadingSuggestions } = trpc.mashupSong.getSuggestions.useQuery(
    {
      songId: excludeSongId || "",
      bpmRange: 8,
      limit: 10,
    },
    {
      enabled: trackType === "instrumental" && !!excludeSongId && !!sourceBpm,
    },
  )

  // Regular song list for vocals or when no suggestions available
  const { data: songsData, isLoading: loadingSongs } = trpc.mashupSong.list.useQuery({
    status: "ready",
    search: search || undefined,
    limit: 20,
  })

  const isLoading = loadingSuggestions || loadingSongs

  // Use suggestions for instrumental if available, otherwise use regular list
  const availableSongs =
    trackType === "instrumental" && suggestionsData?.suggestions ? suggestionsData.suggestions : songsData?.songs || []

  // Filter out excluded song
  const filteredSongs = availableSongs.filter((s) => s.id !== excludeSongId)

  const handleSelect = (song: Song) => {
    onSelect(song)
    setIsOpen(false)
    setSearch("")
  }

  const getBpmBadgeColor = (songBpm: number | null, targetBpm: number | null) => {
    if (!songBpm || !targetBpm) return "bg-secondary"
    const diff = Math.abs(songBpm - targetBpm)
    if (diff <= 2) return "bg-green-500 text-white"
    if (diff <= 5) return "bg-yellow-500 text-white"
    if (diff <= 10) return "bg-orange-500 text-white"
    return "bg-red-500 text-white"
  }

  return (
    <div className="relative">
      {/* Selected song display / Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-3 border rounded-lg text-left transition-colors ${
          isOpen ? "border-primary" : "border-border hover:border-primary/50"
        }`}
      >
        {selectedSong ? (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg shrink-0">
              <Music className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{selectedSong.title}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {selectedSong.artist && <span>{selectedSong.artist}</span>}
                <span className="px-1.5 py-0.5 bg-secondary rounded">{selectedSong.bpm?.toFixed(1) || "--"} BPM</span>
                {selectedSong.camelotKey && (
                  <span className="px-1.5 py-0.5 bg-secondary rounded">{selectedSong.camelotKey}</span>
                )}
              </div>
            </div>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </div>
        ) : (
          <div className="flex items-center justify-between text-muted-foreground">
            <span>Select a song...</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-popover border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search songs..."
                className="pl-8 h-8"
                autoFocus
              />
            </div>
          </div>

          {/* Song List */}
          <div className="overflow-y-auto max-h-60">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSongs.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <p className="text-sm">No songs found</p>
                <p className="text-xs mt-1">Upload songs to get started</p>
              </div>
            ) : (
              <div className="py-1">
                {/* Suggestions header for instrumental */}
                {trackType === "instrumental" && suggestionsData?.suggestions && (
                  <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                    BPM-Matched Suggestions
                  </div>
                )}

                {filteredSongs.map((song) => {
                  const isSelected = selectedSong?.id === song.id
                  const compatScore = "compatibilityScore" in song ? (song as any).compatibilityScore : null

                  return (
                    <button
                      key={song.id}
                      onClick={() => handleSelect(song as Song)}
                      className={`w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-3 ${
                        isSelected ? "bg-accent" : ""
                      }`}
                    >
                      <div className="p-1.5 bg-primary/10 rounded shrink-0">
                        <Music className="h-3 w-3 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate text-sm">{song.title}</p>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {song.artist && <span className="truncate max-w-[100px]">{song.artist}</span>}
                          <span
                            className={`px-1 py-0.5 rounded text-[10px] ${getBpmBadgeColor(
                              song.bpm,
                              sourceBpm || null,
                            )}`}
                          >
                            {song.bpm?.toFixed(1) || "--"} BPM
                          </span>
                          {song.camelotKey && (
                            <span className="px-1 py-0.5 bg-secondary rounded text-[10px]">{song.camelotKey}</span>
                          )}
                          {compatScore !== null && (
                            <span className="px-1 py-0.5 bg-green-500/10 text-green-500 rounded text-[10px]">
                              {compatScore}% match
                            </span>
                          )}
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Clear selection */}
          {selectedSong && (
            <div className="p-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  onSelect(null)
                  setIsOpen(false)
                }}
              >
                Clear Selection
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
